import React, { Dispatch, ReactElement, ReactNode, createContext, useId, useReducer, useRef, useState } from "react";
import { useContext } from "react";
import { Modal, ModalRef, useModalHide } from "./Modal";
import { useCallback } from "react";

export interface Dialog<T> {
    id: string
    title?: ReactNode
    body: ReactElement
    buttons: (setResult: SetResult<T>) => ReactElement
    ok: Function
}

export type SetResult<T> = (result: T) => void

interface DialogState{
    dialogs?: Dialog<any>[]
}

export const DialogContext = createContext<{state: DialogState; dispatch?: Dispatch<Action> }>({state: {dialogs:[]}});

type Action = {type: 'showDialog', payload: Dialog<any>} |
    {type: 'hideDialog', payload: string}

function reducer(state:DialogState, action: Action):DialogState{
    switch(action.type){
        case 'showDialog': return {...state, dialogs: [...state.dialogs, action.payload]};
        case 'hideDialog': return {...state, dialogs: state.dialogs.filter(d => d.id != action.payload)};
        default: return state;
    }
}

function DialogBuilder<T>({dialog}:{dialog:Dialog<T>}){
    const {dispatch} = useContext(DialogContext);
    const result = useRef<T>();
    const modalRef = useRef<ModalRef>()

    function setResult(a:any){
        console.log('set result called', a);
        result.current = a;
        modalRef.current.hide();
    }
    
    return <Modal modalRef={modalRef} title={dialog.title} onHide={()=> {dialog.ok(result.current); dispatch({type:"hideDialog", payload: dialog.id})}}>
            {dialog.body}
            {dialog.buttons(setResult)}
        </Modal>
}

export function DialogStore({children}:{children:ReactNode}){
    const[state, dispatch] = useReducer(reducer, {dialogs:[]})

    return <DialogContext.Provider value={{state, dispatch}}>
        {children}
        {state.dialogs.map(d => <DialogBuilder key={d.id} dialog={d} />)}
    </DialogContext.Provider>
}


export function useShowDialog<T>(dialog:Omit<Dialog<T>, "id" | "ok" | "result">, deps:any[]):()=>Promise<T>{
    const ctx = useContext(DialogContext);
    const id = useId();
    return useCallback(async function():Promise<any> {
        return new Promise((ok) => {
            ctx.dispatch({type: 'showDialog', payload: {id, ok, ...dialog}})
        });
    }, [deps])
}

export function useConfirm(question:ReactNode, {ok = "Sim", buttonClass = "btn-primary", title = null}:{ok?:ReactNode, buttonClass?:string, title?:ReactNode}){
    return useShowDialog<boolean>({
        title,
        body: <>{question}</>,
        buttons: (s) => <div className="text-end mt-4">
            <button className="btn btn-outline-primary me-2" onClick={() => s(false)}>Cancelar</button>
            <button className={`btn ${buttonClass}`} onClick={() => s(true)}>{ok}</button>
        </div>
    }, [question, ok]);
}

export function useAlert(message:ReactNode, {ok = "ok", buttonClass = "btn-primary", title = null}:{ok?:ReactNode, buttonClass?:string, title?:ReactNode}){
    return useShowDialog<boolean>({
        title,
        body: <>{message}</>,
        buttons: (s) => <div className="text-end mt-4">
            <button className={`btn ${buttonClass}`} onClick={() => s(true)}>{ok}</button>
        </div>
    }, [message, ok]);
}

function Prompt({s, ok, buttonClass, placeholder, value}:{s:SetResult<string>, ok: ReactNode, buttonClass:string, placeholder?:string, value?:string}){
    const [text, setText] = useState(value || "");

    return <>
        <input className="form-control mt-2" placeholder={placeholder} value={text} onChange={(e) => setText(e.target.value)} />
        <div className="text-end mt-4">
            <button className="btn btn-outline-primary me-2" onClick={() => s(null)}>Cancelar</button>
            <button className={`btn ${buttonClass}`} onClick={() => s(text)}>{ok}</button>
        </div>
    </>
}

export function usePrompt(question:ReactNode, {ok = "Ok", buttonClass = "btn-primary", placeholder = "", value = ""}){
    return useShowDialog<string>({
        body: <>{question}</>,
        buttons: (s) => <Prompt {...{buttonClass, placeholder, value, s, ok}} />
    }, [question, ok, value]);
}